import React from 'react';
import PropTypes from 'prop-types';

import { ApIcon, colors } from '@alixpartners/ui-components';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { UNIT_MAP } from 'utils/number';

import {
	Wrapper,
	ListItem,
	ListValue,
	ListIndex,
	ListLabel,
	ListCellWrapper,
	IconWrapper,
	ListInputCell,
	ListHeader,
	StyledButton,
	StyledInput,
	ErrorIconWrapper,
} from './Segments.styles';

const MAX_SEGMENTS = 6;
const VALIDATION_FIELDS = {
	label: 'label',
	end: 'end',
};

class Segments extends React.Component {
	state = {
		segments: [],
		max: 0,
		min: 0,
	};

	componentDidMount() {
		this.setStateFromProps();
	}

	componentDidUpdate(prevProps) {
		if (
			!isEqual(this.props.segments, prevProps.segments) ||
			!isEqual(this.props.max, prevProps.max) ||
			!isEqual(this.props.min, prevProps.min) ||
			!isEqual(this.props.unit, prevProps.unit)
		) {
			this.setStateFromProps();
		}
	}

	formatSpend = value => {
		const { unit } = this.props;
		const unitRatio = UNIT_MAP[unit] || 1;

		return parseFloat(value / unitRatio);
	};

	setStateFromProps = () => {
		const { segments, max, min } = this.props;
		const formattedMin = this.formatSpend(min);
		const formattedMax = this.formatSpend(max);
		const defaultSegment = {
			name: 'All',
			start: formattedMin,
			end: formattedMax,
		};

		const segmentsFormatted = segments.map(segment => ({
			...segment,
			start: this.formatSpend(segment.start),
			end: this.formatSpend(segment.end),
		}));

		this.setState(
			{
				segments: isEmpty(segments) ? [defaultSegment] : segmentsFormatted,
				max: formattedMax,
				min: formattedMin,
			},
			this.doInitialValidation
		);
	};

	doInitialValidation = () => {
		const { onValidationError } = this.props;
		this.validate(() => {
			if (!this.isValid()) {
				onValidationError();
			}
		});
	};

	validate = callback => {
		const { segments } = this.state;
		const labelsListWithoutCurrent = currentIndex =>
			segments
				.filter((item, index) => index !== currentIndex)
				.map(item => item.name);

		const validatedSegments = segments.map((segment, index) => {
			let validatedSegment = { ...segment, errors: [] };

			if (
				!segment.name ||
				labelsListWithoutCurrent(index).includes(segment.name)
			) {
				validatedSegment.errors.push(VALIDATION_FIELDS.label);
			}

			if (
				// do not validate the last element, as it is not editable
				index !== segments.length - 1 &&
				(segment.start > segment.end ||
					segment.start === '' ||
					segment.end === '')
			) {
				validatedSegment.errors.push(VALIDATION_FIELDS.end);
			}

			// for segment second from the end - show validation errors, related to last element
			// as last segment value is not editable
			if (index === segments.length - 2) {
				const lastSegment = segments[segments.length - 1];

				if (
					lastSegment &&
					(lastSegment.start > lastSegment.end ||
						lastSegment.start === '' ||
						lastSegment.end === '')
				) {
					validatedSegment.errors.push(VALIDATION_FIELDS.end);
				}
			}

			if (validatedSegment.errors.length > 0) {
				return validatedSegment;
			}

			const { errors, ...withoutErrors } = validatedSegment;

			return withoutErrors;
		});

		this.setState({ segments: validatedSegments }, callback);
	};

	isValid = () => {
		const { segments } = this.state;

		return !segments.find(segment => segment.errors);
	};

	updateData = () => {
		const { onChange, onValidationError, unit } = this.props;
		const unitRatio = UNIT_MAP[unit] || 1;

		this.validate(() => {
			if (this.isValid()) {
				const { segments } = this.state;

				const rawSegments = segments.map(segment => ({
					...segment,
					start: segment.start * unitRatio,
					end: segment.end * unitRatio,
				}));

				onChange({ segments: rawSegments });
			} else {
				onValidationError();
			}
		});
	};

	changeLabel = (e, editedIndex) => {
		const { segments } = this.state;
		const name = e.target ? e.target.value : e;
		const updatedSegments = segments.map(
			(segment, index) =>
				editedIndex === index ? { ...segment, name } : segment
		);

		this.setState({ segments: updatedSegments }, this.updateData);
	};

	changeEnd = (e, editedIndex) => {
		const { segments } = this.state;
		const value = e.target ? e.target.value : e;
		const formattedValue = value !== '' ? Number(value) : value;

		const updatedSegments = segments.map((segment, index) => {
			if (editedIndex === index) {
				return { ...segment, end: formattedValue };
			}

			if (editedIndex + 1 === index) {
				return { ...segment, start: formattedValue };
			}

			return segment;
		});

		this.setState(
			{ segments: updatedSegments, autofocusOn: false },
			this.updateData
		);
	};

	removeItem = deleteIndex => {
		const { segments, max, min } = this.state;
		const deletedItem = segments[deleteIndex];
		let updatedSegments = segments.filter(
			(segment, index) => deleteIndex !== index
		);

		if (deleteIndex === segments.length - 1) {
			updatedSegments = updatedSegments.map((segment, index) => {
				if (index === updatedSegments.length - 1) {
					return { ...segment, end: max };
				}

				return segment;
			});
		} else if (deleteIndex === 0) {
			updatedSegments = updatedSegments.map((segment, index) => {
				if (index === 0) {
					return { ...segment, start: min };
				}

				return segment;
			});
		} else {
			updatedSegments = updatedSegments.map((segment, index) => {
				if (index === deleteIndex) {
					return { ...segment, start: deletedItem.start };
				}

				return segment;
			});
		}

		this.setState(
			{ segments: updatedSegments, autofocusOn: false },
			this.updateData
		);
	};

	getUniqueSegmentName = () => {
		const { segments } = this.state;
		const segmentsNames = segments.map(segment => segment.name);
		const nextStart =
			(!segments ||
			!segments.length ||
			segments[segments.length - 1].start === Number.NEGATIVE_INFINITY
				? 0
				: segments[segments.length - 1].start) || 0;

		let name = `${nextStart}-Max`;
		let counter = 0;

		while (segmentsNames.includes(name)) {
			name = `${nextStart}-Max(${counter})`;
			counter += 1;
		}

		return name;
	};

	addItem = () => {
		const { segments, max } = this.state;
		const nextStart =
			(segments[segments.length - 1].start === Number.NEGATIVE_INFINITY
				? 0
				: segments[segments.length - 1].start) || 0;
		const name = this.getUniqueSegmentName();

		const newItem = {
			name,
			start: nextStart,
			end: max,
		};

		const updatedSegments = segments.map((segment, index) => {
			return index === segments.length - 1
				? { ...segment, end: segment.start || 0 }
				: segment;
		});

		// fix end date to zero when adding first not-default segment
		if (segments.length === 1) {
			updatedSegments[0] = { ...updatedSegments[0], end: 0 };
		}

		this.setState(
			{ segments: [...updatedSegments, newItem], autofocusOn: true },
			this.updateData
		);
	};

	focusTextInput = textInput => textInput && textInput.focus();

	render() {
		const { segments, autofocusOn } = this.state;
		const { currency, unit } = this.props;
		const lastSegment = segments[segments.length - 2];
		const isAddBtnDisabled =
			segments.length === MAX_SEGMENTS ||
			(!!lastSegment && lastSegment.start === lastSegment.end);

		const currencyUnitLabel = `${currency}${unit}`;

		return (
			<Wrapper>
				<ListItem>
					<ListIndex />
					<ListValue>
						<ListLabel>
							<ListHeader>Name</ListHeader>
						</ListLabel>
						<ListInputCell>
							<ListHeader>Low</ListHeader>
						</ListInputCell>
						<ListInputCell>
							<ListHeader>High</ListHeader>
						</ListInputCell>
						<ListCellWrapper>
							<IconWrapper />
						</ListCellWrapper>
					</ListValue>
				</ListItem>
				{segments.map(({ name, start, end, errors }, index) => {
					const endHasError = errors && errors.includes(VALIDATION_FIELDS.end);

					return (
						<ListItem key={index}>
							{endHasError ? (
								<ErrorIconWrapper>
									<ApIcon iconName="link_off" iconColor={colors.red1} />
								</ErrorIconWrapper>
							) : (
								<ListIndex>{index + 1}</ListIndex>
							)}

							<ListValue>
								<ListLabel>
									<StyledInput
										hasError={
											errors && errors.includes(VALIDATION_FIELDS.label)
										}
										value={name}
										name={index}
										onChange={e => this.changeLabel(e, index)}
									/>
								</ListLabel>

								<ListInputCell>
									<StyledInput
										value={start}
										name={index}
										disabled
										type="number"
										placeholder="Min"
										symbol={currencyUnitLabel}
									/>
								</ListInputCell>

								<ListInputCell>
									<StyledInput
										hasError={endHasError}
										value={end}
										name={index}
										onChange={e => this.changeEnd(e, index)}
										placeholder="Max"
										innerRef={
											index === segments.length - 2 && autofocusOn
												? this.focusTextInput
												: null
										}
										disabled={index === segments.length - 1}
										type="number"
										symbol={currencyUnitLabel}
									/>
								</ListInputCell>
								<ListCellWrapper>
									<IconWrapper>
										{segments.length > 1 && (
											<ApIcon
												iconName="delete_outline"
												iconColor={colors.grey8}
												onClick={() => this.removeItem(index)}
											/>
										)}
									</IconWrapper>
								</ListCellWrapper>
							</ListValue>
						</ListItem>
					);
				})}
				<StyledButton disabled={isAddBtnDisabled} onClick={this.addItem}>
					+ Add segment
				</StyledButton>
			</Wrapper>
		);
	}
}

Segments.propTypes = {
	segments: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			start: PropTypes.number,
			end: PropTypes.number,
		})
	).isRequired,
	onChange: PropTypes.func.isRequired,
	onValidationError: PropTypes.func.isRequired,
	max: PropTypes.number.isRequired,
	unit: PropTypes.string.isRequired,
	currency: PropTypes.string.isRequired,
};

export default Segments;
